import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useAuthStore } from './auth.store'

const STORAGE_KEY_PREFIX = 'cf-device-config'

interface DeviceConfigStorage {
  config: Dictionary<any>
  lastUpdated: string
}

/**
 * Device Config Store
 * Stores configuration settings per device per user using localStorage
 * Same API as user-config.store but persists to browser storage instead of backend
 * Config is stored separately for each user (keyed by sub from JWT)
 */
export const useDeviceConfigStore = defineStore('device.config', () => {
  const deviceConfig = ref<Dictionary<any>>({})
  const lastUpdated = ref<Date>()
  const isInitialized = ref(false)
  const currentUserSub = ref<string>()

  const authStore = useAuthStore()

  /**
   * Get storage key for current user
   */
  function getStorageKey(): string {
    const sub = currentUserSub.value || authStore.user?.sub
    return sub ? `${STORAGE_KEY_PREFIX}-${sub}` : STORAGE_KEY_PREFIX
  }

  /**
   * Load config from localStorage for current user
   */
  function loadFromStorage() {
    try {
      const storageKey = getStorageKey()
      const stored = localStorage.getItem(storageKey)
      if (!stored) {
        // No data for this user, reset to empty
        deviceConfig.value = {}
        lastUpdated.value = undefined
        return
      }

      const data: DeviceConfigStorage = JSON.parse(stored)
      deviceConfig.value = data.config || {}
      lastUpdated.value = data.lastUpdated
        ? new Date(data.lastUpdated)
        : undefined
    } catch (error) {
      if (process.env.ENV === 'DEV') {
        console.error('[DeviceConfigStore] Failed to load from storage:', error)
      }
      // Reset on error
      deviceConfig.value = {}
      lastUpdated.value = undefined
    }
  }

  /**
   * Save config to localStorage for current user
   */
  function saveToStorage() {
    try {
      const storageKey = getStorageKey()
      const data: DeviceConfigStorage = {
        config: deviceConfig.value,
        lastUpdated: new Date().toISOString(),
      }
      localStorage.setItem(storageKey, JSON.stringify(data))
      lastUpdated.value = new Date()
    } catch (error) {
      if (process.env.ENV === 'DEV') {
        console.error('[DeviceConfigStore] Failed to save to storage:', error)
      }
    }
  }

  /**
   * Initialize the store
   * Loads config from localStorage for current user
   */
  function initialize() {
    if (isInitialized.value) return

    // Set current user sub from auth store
    currentUserSub.value = authStore.user?.sub

    loadFromStorage()
    isInitialized.value = true

    // Watch for user changes and reload config
    watch(
      () => authStore.user?.sub,
      (newSub, oldSub) => {
        if (newSub !== oldSub) {
          currentUserSub.value = newSub
          loadFromStorage() // Load config for new user
        }
      },
    )
  }

  /**
   * Get a config value by key
   * @param key - Config key
   * @param defaultValue - Default value if key doesn't exist
   * @returns Config value or default
   */
  function get<T>(key: string, defaultValue?: T): T | undefined {
    if (deviceConfig.value[key] !== undefined) {
      return deviceConfig.value[key] as T
    }
    return defaultValue
  }

  /**
   * Set a config value
   * @param key - Config key
   * @param value - Config value
   */
  function set<T>(key: string, value: T) {
    deviceConfig.value[key] = value
    saveToStorage()
  }

  /**
   * Remove a config key
   * @param key - Config key to remove
   */
  function remove(key: string) {
    delete deviceConfig.value[key]
    saveToStorage()
  }

  /**
   * Clear device config for current user
   */
  function clear() {
    deviceConfig.value = {}
    lastUpdated.value = undefined
    const storageKey = getStorageKey()
    localStorage.removeItem(storageKey)
  }

  /**
   * Clear device config for all users
   */
  function clearAll() {
    // Find and remove all device config keys
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith(STORAGE_KEY_PREFIX)) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key))

    // Reset current config
    deviceConfig.value = {}
    lastUpdated.value = undefined
  }

  /**
   * Refresh config from localStorage
   * (No-op for compatibility with user-config API, but reloads from storage)
   */
  function refresh() {
    loadFromStorage()
  }

  // Computed properties for common config keys (add as needed)
  const pinnedSpace = computed<string[]>(
    () => deviceConfig.value['pinnedSpace'] || [],
  )
  const pinnedStaff = computed<string[]>(
    () => deviceConfig.value['pinnedStaff'] || [],
  )

  return {
    // Computed properties
    pinnedSpace,
    pinnedStaff,

    // Methods
    get,
    set,
    remove,
    clear,
    clearAll,
    refresh,
    initialize,

    // State
    isInitialized,
    lastUpdated,
    currentUserSub,
  }
})
